import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { PicklistValue } from '@/types'

const PICKLIST_QUERY_KEY = ['picklists']

/**
 * Fetch all picklist values grouped by entity/field.
 * Long staleTime since picklist data changes rarely.
 */
export function usePicklists(includeInactive = false) {
  return useQuery({
    queryKey: [...PICKLIST_QUERY_KEY, { includeInactive }],
    queryFn: () => apiClient.getPicklists(includeInactive),
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    throwOnError: false, // Never crash the app for missing picklist data
  })
}

/**
 * Get active picklist values for a specific entity and field.
 * Returns an empty array while loading.
 */
export function usePicklistValues(entity: string, field: string): PicklistValue[] {
  const { data } = usePicklists()
  return data?.[entity]?.[field]?.filter((v) => v.is_active) ?? []
}

/**
 * Get string array of values for simple dropdowns.
 * e.g., usePicklistOptions('account', 'type') → ['Direct', 'Reseller']
 */
export function usePicklistOptions(entity: string, field: string): string[] {
  const values = usePicklistValues(entity, field)
  return values.map((v) => v.value)
}

/**
 * Get value/label pairs for dropdowns that need custom display labels.
 * e.g., usePicklistOptionPairs('opportunity', 'contract_term') → [{ value: '12', label: '1 Year' }]
 */
export function usePicklistOptionPairs(
  entity: string,
  field: string
): { value: string; label: string }[] {
  const values = usePicklistValues(entity, field)
  return values.map((v) => ({ value: v.value, label: v.label || v.value }))
}

/**
 * Special hook for opportunity stages with probability lookup.
 */
export function useStages() {
  const values = usePicklistValues('opportunity', 'stage_name')
  return {
    stages: values.map((v) => ({
      value: v.value,
      label: v.label || v.value,
      probability: (v.metadata?.probability as number) ?? 0,
    })),
    getProbability: (stage: string) => {
      const found = values.find((v) => v.value === stage)
      return (found?.metadata?.probability as number) ?? 0
    },
  }
}

/**
 * Get the default value for a picklist field.
 */
export function usePicklistDefault(entity: string, field: string): string {
  const values = usePicklistValues(entity, field)
  const defaultValue = values.find((v) => v.is_default)
  return defaultValue?.value ?? values[0]?.value ?? ''
}

// ========== Mutation hooks for admin page ==========

export function useAddPicklistValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      entity,
      field,
      value,
    }: {
      entity: string
      field: string
      value: Partial<PicklistValue>
    }) => apiClient.addPicklistValue(entity, field, value),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PICKLIST_QUERY_KEY }),
  })
}

export function useUpdatePicklistValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<PicklistValue> }) =>
      apiClient.updatePicklistValue(id, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PICKLIST_QUERY_KEY }),
  })
}

export function useReorderPicklistValues() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      entity,
      field,
      order,
    }: {
      entity: string
      field: string
      order: { id: number; sort_order: number }[]
    }) => apiClient.reorderPicklistValues(entity, field, order),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PICKLIST_QUERY_KEY }),
  })
}

export function useDeactivatePicklistValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.deactivatePicklistValue(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PICKLIST_QUERY_KEY }),
  })
}

export function useReactivatePicklistValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.reactivatePicklistValue(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PICKLIST_QUERY_KEY }),
  })
}
